import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import "flag-icons/css/flag-icons.min.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import {
  Globe,
  Server,
  Search,
  Radio,
  Route as RouteIcon,
  FileSearch,
  ChevronDown,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Network,
  Activity,
  ArrowRight,
  Copy,
  RefreshCw,
  ArrowDown,
  Download,
  Gauge,
  ExternalLink,
  Shield,
  ShieldCheck,
  ShieldX,
  Layers,
  Share2,
  Monitor,
  ArrowUp,
  Info
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";

// Empty/unset => relative "/api" (same-origin, proxied by nginx). This keeps the
// frontend working regardless of the server's IP — avoid baking an IP into the build.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// =============================================================================
// i18n — browser-language default (TR/EN), toggle, t()
// =============================================================================
const TRANSLATIONS = {
  tr: {
    nav_tools: "Test Araçları", nav_netinfo: "Ağ Bilgileri", nav_comm: "BGP Communities",
    hero_label: "NETLEN NETWORK • AS44620", hero_label_speed: "NETLEN LOOKING GLASS",
    hero_title_1: "Ağımızı keşfedin.", hero_title_2: "Bağlantınızı test edin.",
    hero_title_speed: "Bağlantınızın hızını ölçün.",
    hero_sub: "Gecikme, rota ve bağlantı performansını Netlen ağı üzerinden inceleyin.",
    hero_sub_speed: "Netlen İstanbul sunucusuna olan indirme, yükleme ve gecikme değerlerinizi test edin.",
    pill_loc: "İstanbul, Türkiye",
    f_router: "Router", f_ipver: "IP Version",
    ph_bgp: "IP prefix girin (örn. 8.8.8.0/24)", ph_whois: "IP, ASN veya alan adı girin", ph_default: "IP adresi veya alan adı girin",
    btn_execute: "Çalıştır", btn_running: "Çalışıyor...",
    empty_title: "Sorguya hazır", empty_sub: "Bir araç seçip hedef girin",
    executing: "Çalışıyor",
    copy: "Kopyala", download: "İndir", copied: "Kopyalandı!", no_output: "Çıktı yok",
    raw_output: "Ham Çıktı", bgp_raw: "BGP Rota Çıktısı", whois_raw: "Ham Whois Çıktısı",
    // ping
    transmitted: "Gönderilen", received: "Alınan", packet_loss: "Paket Kaybı", avg_rtt: "Ort. RTT",
    rtt_stats: "RTT İstatistikleri", min: "Min", avg: "Ort", max: "Maks", mdev: "Sapma",
    // mtr
    hop: "Atlama", host: "Sunucu", loss: "Kayıp%", last: "Son", best: "En iyi", worst: "En kötü",
    // bgp
    prefix_announced: "Duyurulan Prefix", source_as: "Kaynak AS", origin: "Origin", rpki: "RPKI",
    paths_title: "Yollar — Bizden Hedefe", path_unit: "yol",
    selected: "Seçili", alternative: "Alternatif", scheme: "Şema", community: "Community",
    no_routes: "Rota bulunamadı",
    rpki_valid: "Valid", rpki_invalid: "Invalid", rpki_notfound: "Not Found",
    // whois
    parsed_info: "Ayrıştırılmış Bilgi",
    // as path graph
    aspath_title: "AS Path Görselleştirme", aspath_sub: "Upstream yönünde trafik akışı",
    aspath_downloaded: "AS Path grafiği indirildi!", aspath_fail: "İndirme başarısız oldu",
    origin_as: "Origin AS", no_aspath: "AS path verisi yok",
    // speedtest
    st_server: "İstanbul • IST-01", st_server_sub: "Test sunucusu: Netlen",
    st_browser: "Tarayıcınız ↔ Netlen İstanbul", st_browser_sub: "Bu test tarayıcınızdan Netlen sunucusuna yapılır.",
    st_https_sub: "Güvenli bağlantı ile test edilir.",
    st_sample: "Örnek test sonucu", st_done: "Test tamamlandı", st_ready: "Hazır",
    st_measuring_ping: "Gecikme ölçülüyor…", st_measuring_down: "İndirme test ediliyor…", st_measuring_up: "Yükleme test ediliyor…",
    st_down: "İndirme", st_up: "Yükleme", st_ping: "Ping", st_jitter: "Jitter", st_ploss: "Paket kaybı",
    st_down_speed: "İndirme hızı", st_up_speed: "Yükleme hızı",
    st_start: "Testi Başlat", st_restart: "Testi Tekrarla", st_stop: "Durdur",
    st_share: "Sonucu paylaş", st_png: "PNG indir", st_shared: "Sonuç kopyalandı!", st_png_done: "PNG indirildi",
    st_files: "Test dosyaları", st_files_sub: "Bağlantınızı dosya indirerek de kontrol edebilirsiniz.",
    st_tip_title: "Daha doğru sonuç için", st_tip_sub: "VPN'i kapatın, arka plandaki indirmeleri durdurun ve mümkünse kablolu bağlantı kullanın.",
    st_file_missing: "Test dosyası yok (HTML döndü).",
    // sections
    net_title: "Netlen ağ bilgileri", net_sub: "Bağlantı ve peering için ihtiyaç duyduğunuz bilgiler.",
    comm_sub: "Yönlendirme politikalarımızı ve community açıklamalarını inceleyin.", comm_search: "Community ara...",
    comm_all: "Tümü", cat_info: "Bilgilendirme", cat_lp: "Local Preference", cat_prepend: "Prepend", cat_blackhole: "Blackhole",
    th_community: "Community", th_category: "Kategori", th_usage: "Kullanım", th_desc: "Açıklama", no_results: "Sonuç bulunamadı",
    footer_copy: "Netlen Internet Hizmetleri", footer_peering: "Peering", footer_contact: "İletişim", footer_terms: "Kullanım Koşulları",
    result_failed: "Sorgu başarısız", results: "Sonuçları", refresh: "Yenile",
    img_copied: "Görsel kopyalandı", img_downloaded: "Görsel indirildi", img_downloaded_https: "Görsel indirildi (kopyalama için HTTPS gerekli)", img_fail: "İşlem başarısız",
    png_fail: "İndirme başarısız", routers_fail: "Router listesi yüklenemedi", enter_target: "Lütfen bir hedef girin", select_router: "Lütfen bir router seçin", query_ok: "Sorgu tamamlandı", cmd_bidir: "Çift yön",
  },
  en: {
    nav_tools: "Test Tools", nav_netinfo: "Network Info", nav_comm: "BGP Communities",
    hero_label: "NETLEN NETWORK • AS44620", hero_label_speed: "NETLEN LOOKING GLASS",
    hero_title_1: "Explore our network.", hero_title_2: "Test your connection.",
    hero_title_speed: "Measure your connection speed.",
    hero_sub: "Inspect latency, routing and connection performance across the Netlen network.",
    hero_sub_speed: "Test your download, upload and latency to the Netlen Istanbul server.",
    pill_loc: "Istanbul, Türkiye",
    f_router: "Router", f_ipver: "IP Version",
    ph_bgp: "Enter IP prefix (e.g. 8.8.8.0/24)", ph_whois: "Enter IP, ASN or domain", ph_default: "Enter IP address or hostname",
    btn_execute: "Execute", btn_running: "Running...",
    empty_title: "Ready to query", empty_sub: "Select a tool and enter a target",
    executing: "Executing",
    copy: "Copy", download: "Download", copied: "Copied!", no_output: "No output",
    raw_output: "Raw Output", bgp_raw: "BGP Route Output", whois_raw: "Raw Whois Output",
    transmitted: "Transmitted", received: "Received", packet_loss: "Packet Loss", avg_rtt: "Avg RTT",
    rtt_stats: "RTT Statistics", min: "Min", avg: "Avg", max: "Max", mdev: "Mdev",
    hop: "Hop", host: "Host", loss: "Loss%", last: "Last", best: "Best", worst: "Worst",
    prefix_announced: "Announced Prefix", source_as: "Source AS", origin: "Origin", rpki: "RPKI",
    paths_title: "Paths — Us to Destination", path_unit: "paths",
    selected: "Selected", alternative: "Alternative", scheme: "Graph", community: "Community",
    no_routes: "No routes found",
    rpki_valid: "Valid", rpki_invalid: "Invalid", rpki_notfound: "Not Found",
    parsed_info: "Parsed Information",
    aspath_title: "AS Path Visualization", aspath_sub: "Traffic flow upstream",
    aspath_downloaded: "AS Path graph downloaded!", aspath_fail: "Download failed",
    origin_as: "Origin AS", no_aspath: "No AS path data available",
    st_server: "Istanbul • IST-01", st_server_sub: "Test server: Netlen",
    st_browser: "Your browser ↔ Netlen Istanbul", st_browser_sub: "This test runs from your browser to the Netlen server.",
    st_https_sub: "Tested over a secure connection.",
    st_sample: "Sample test result", st_done: "Test complete", st_ready: "Ready",
    st_measuring_ping: "Measuring latency…", st_measuring_down: "Testing download…", st_measuring_up: "Testing upload…",
    st_down: "Download", st_up: "Upload", st_ping: "Ping", st_jitter: "Jitter", st_ploss: "Packet loss",
    st_down_speed: "Download speed", st_up_speed: "Upload speed",
    st_start: "Start Test", st_restart: "Run Again", st_stop: "Stop",
    st_share: "Share result", st_png: "Download PNG", st_shared: "Result copied!", st_png_done: "PNG downloaded",
    st_files: "Test files", st_files_sub: "You can also check your connection by downloading a file.",
    st_tip_title: "For a more accurate result", st_tip_sub: "Turn off VPN, stop background downloads and use a wired connection if possible.",
    st_file_missing: "Test file missing (got HTML).",
    net_title: "Netlen network info", net_sub: "Everything you need for connectivity and peering.",
    comm_sub: "Explore our routing policies and community descriptions.", comm_search: "Search community...",
    comm_all: "All", cat_info: "Informational", cat_lp: "Local Preference", cat_prepend: "Prepend", cat_blackhole: "Blackhole",
    th_community: "Community", th_category: "Category", th_usage: "Usage", th_desc: "Description", no_results: "No results found",
    footer_copy: "Netlen Internet Hizmetleri", footer_peering: "Peering", footer_contact: "Contact", footer_terms: "Terms of Use",
    result_failed: "Query failed", results: "Results", refresh: "Refresh",
    img_copied: "Image copied", img_downloaded: "Image downloaded", img_downloaded_https: "Image downloaded (HTTPS required to copy)", img_fail: "Operation failed",
    png_fail: "Download failed", routers_fail: "Failed to load routers", enter_target: "Please enter a target", select_router: "Please select a router", query_ok: "Query completed successfully", cmd_bidir: "Bidirectional",
  },
};
const detectLang = () => {
  try {
    const saved = localStorage.getItem("lg_lang");
    if (saved === "tr" || saved === "en") return saved;
  } catch (_) { /* ignore */ }
  try {
    return (navigator.language || "en").toLowerCase().startsWith("tr") ? "tr" : "en";
  } catch (_) { return "en"; }
};
let CURRENT_LANG = detectLang();
const t = (key) => (TRANSLATIONS[CURRENT_LANG] && TRANSLATIONS[CURRENT_LANG][key]) || TRANSLATIONS.tr[key] || key;

// Tool configurations
const TOOLS = [
  { id: "ping", name: "Ping", icon: Radio, description: "ICMP ping test" },
  { id: "mtr", name: "MTR", icon: Activity, description: "Traceroute with statistics" },
  { id: "bgp-route", name: "BGP Route", icon: RouteIcon, description: "BGP route lookup" },
  { id: "whois", name: "Whois", icon: FileSearch, description: "WHOIS query" },
  { id: "speedtest", name: "Speedtest", icon: Gauge, description: "Internet speed test" },
];

// Country flag (SVG via flag-icons). cc = ISO alpha-2 (e.g. "TR", "US").
const Flag = ({ cc, className = "" }) =>
  cc ? (
    <span
      className={`fi fi-${cc.toLowerCase()} ${className}`}
      title={cc}
      style={{ borderRadius: 2, flexShrink: 0 }}
    />
  ) : null;

// AS Path Graph Component - Oval Box Style with download feature
const ASPathGraph = ({ data }) => {
  const containerRef = useRef(null);
  const routes = data?.routes || [];
  
  // Only show best/selected route
  const bestRoute = routes.find(r => r.selected) || routes[0];
  
  const downloadAsImage = async () => {
    if (!containerRef.current) return;
    
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `as-path-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('AS Path grafiği indirildi!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('İndirme başarısız oldu');
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-lg overflow-hidden" data-testid="as-path-graph">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">AS Path Visualization</h3>
          <p className="text-xs text-gray-500 mt-0.5">Upstream yönünde trafik akışı</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={downloadAsImage}
          className="h-8 text-xs"
          data-testid="download-graph-btn"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          İndir
        </Button>
      </div>
      <div className="p-6" ref={containerRef}>
        {bestRoute ? (
          <div className="flex flex-col items-center">
            {(bestRoute.as_path || []).map((asn, hopIndex) => {
              const asName = bestRoute.as_names?.[hopIndex] || `AS${asn}`;
              const asDesc = bestRoute.as_descriptions?.[hopIndex] || '';
              const asCC = bestRoute.as_countries?.[hopIndex];
              const isLast = hopIndex === (bestRoute.as_path || []).length - 1;
              
              return (
                <div key={hopIndex} className="flex flex-col items-center">
                  {/* AS Oval Box */}
                  <div 
                    className="w-32 px-4 py-3 rounded-full bg-primary text-white text-center shadow-md border-2 border-primary/80"
                  >
                    <a
                      href={`https://bgp.tools/as/${asn}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`bgp.tools/as/${asn} (yeni sekmede açılır)`}
                      className="font-mono font-bold text-sm inline-flex items-center justify-center gap-1 hover:underline"
                    >
                      AS{asn}
                      <ExternalLink className="w-3 h-3 opacity-80" />
                    </a>
                    <div className="text-xs opacity-90 truncate flex items-center justify-center gap-1" title={asDesc || asName}>
                      <Flag cc={asCC} />
                      <span className="truncate">{asName.length > 14 ? asName.substring(0, 12) + '..' : asName}</span>
                    </div>
                  </div>
                  
                  {/* Arrow connector */}
                  {!isLast && (
                    <div className="flex flex-col items-center my-1">
                      <div className="w-0.5 h-5 bg-primary"></div>
                      <ArrowDown className="w-5 h-5 -mt-1.5 text-primary" />
                      <div className="w-0.5 h-3 -mt-1.5 bg-primary"></div>
                    </div>
                  )}
                  
                  {/* Origin label for last item */}
                  {isLast && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      Origin AS
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-gray-400">
            <div className="text-center">
              <Network className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No AS path data available</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Terminal Output Component
const TerminalOutput = ({ content, title }) => {
  const terminalRef = useRef(null);

  const copyAsImage = async () => {
    if (!terminalRef.current) return;
    try {
      const canvas = await html2canvas(terminalRef.current, {
        backgroundColor: '#1c1c1c',
        scale: 2,
        logging: false
      });
      
      canvas.toBlob(async (blob) => {
        if (blob) {
          try {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob })
            ]);
            toast.success(t("img_copied"));
          } catch (err) {
            // HTTPS gerekli - fallback olarak indir
            const link = document.createElement('a');
            link.download = `${title || 'output'}-${Date.now()}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            toast.success(t("img_downloaded_https"));
          }
        }
      }, 'image/png');
    } catch (err) {
      toast.error(t("img_fail"));
    }
  };

  const downloadAsImage = async () => {
    if (!terminalRef.current) return;
    try {
      const canvas = await html2canvas(terminalRef.current, {
        backgroundColor: '#1c1c1c',
        scale: 2,
        logging: false
      });
      const link = document.createElement('a');
      link.download = `${title || 'output'}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(t("img_downloaded"));
    } catch (err) {
      toast.error(t("img_fail"));
    }
  };

  return (
    <div className="relative" data-testid="terminal-output">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-600">{title || "Output"}</span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={copyAsImage}
            className="h-7 px-2"
            data-testid="copy-image-btn"
          >
            <Copy className="h-3.5 w-3.5 mr-1" />
            {t("copy")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={downloadAsImage}
            className="h-7 px-2"
            data-testid="download-image-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            {t("download")}
          </Button>
        </div>
      </div>
      {/* macOS style terminal window */}
      <div className="rounded-lg overflow-hidden shadow-lg border border-gray-700" ref={terminalRef}>
        {/* Window title bar */}
        <div className="bg-[#2d2d2d] px-4 py-2.5 flex items-center gap-2">
          {/* Traffic light buttons */}
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
            <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
            <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
          </div>
          {/* Window title */}
          <div className="flex-1 text-center">
            <span className="text-gray-400 text-xs font-medium">
              Netlen - AS44620 Looking Glass — {title}
            </span>
          </div>
          {/* Spacer for symmetry */}
          <div className="w-14"></div>
        </div>
        {/* Terminal content */}
        <div className="bg-[#1c1c1c] p-4">
          <pre className="text-gray-200 text-sm font-mono whitespace-pre-wrap">{content || t("no_output")}</pre>
        </div>
      </div>
    </div>
  );
};

// Ping Result Component
const PingResult = ({ data, router, target }) => {
  if (!data) return null;

  const stats = data.statistics || {};
  const packets = data.packets || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="ping-result"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{t("transmitted")}</p>
            <p className="text-2xl font-bold text-gray-900">{stats.transmitted || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{t("received")}</p>
            <p className="text-2xl font-bold text-green-600">{stats.received || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{t("packet_loss")}</p>
            <p className="text-2xl font-bold text-gray-900">{stats.packet_loss || 0}%</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{t("avg_rtt")}</p>
            <p className="text-2xl font-bold text-primary">{stats.rtt?.avg?.toFixed(2) || '-'} ms</p>
          </CardContent>
        </Card>
      </div>

      {stats.rtt && (
        <Card className="bg-white">
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">{t("rtt_stats")}</h4>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500">{t("min")}</p>
                <p className="font-mono text-sm font-semibold">{stats.rtt.min?.toFixed(2)} ms</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t("avg")}</p>
                <p className="font-mono text-sm font-semibold text-primary">{stats.rtt.avg?.toFixed(2)} ms</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t("max")}</p>
                <p className="font-mono text-sm font-semibold">{stats.rtt.max?.toFixed(2)} ms</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t("mdev")}</p>
                <p className="font-mono text-sm font-semibold">{stats.rtt.mdev?.toFixed(2)} ms</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <TerminalOutput content={data.raw} title={t("raw_output")} />
    </motion.div>
  );
};

// MTR Result Component
const MTRResult = ({ data, router, target }) => {
  if (!data) return null;

  const hops = data.hops || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="mtr-result"
    >
      <Card className="bg-white overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-12 font-semibold">{t("hop")}</TableHead>
                <TableHead className="font-semibold">{t("host")}</TableHead>
                <TableHead className="text-right font-semibold">{t("loss")}</TableHead>
                <TableHead className="text-right font-semibold">{t("last")}</TableHead>
                <TableHead className="text-right font-semibold">{t("avg")}</TableHead>
                <TableHead className="text-right font-semibold">{t("best")}</TableHead>
                <TableHead className="text-right font-semibold">{t("worst")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hops.map((hop, idx) => (
                <TableRow key={idx} className="hover:bg-gray-50">
                  <TableCell className="font-mono text-sm">{hop.hop}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {!hop.host || hop.host === "???" ? (
                      <span className="text-gray-400">* * *</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <Flag cc={hop.cc} />
                        {hop.host}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono text-sm ${hop.loss_percent > 0 ? 'text-red-500' : 'text-green-600'}`}>
                      {hop.loss_percent}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{hop.last_ms?.toFixed(1)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-primary font-semibold">{hop.avg_ms?.toFixed(1)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-green-600">{hop.best_ms?.toFixed(1)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-orange-500">{hop.worst_ms?.toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TerminalOutput content={data.raw} title={t("raw_output")} />
    </motion.div>
  );
};

// RPKI validation badge
const RpkiBadge = ({ rpki }) => {
  if (!rpki) return null;
  const map = {
    valid: { cls: "bg-green-100 text-green-700", icon: <ShieldCheck className="w-3 h-3 mr-1" />, label: t("rpki_valid") },
    invalid: { cls: "bg-red-100 text-red-700", icon: <ShieldX className="w-3 h-3 mr-1" />, label: t("rpki_invalid") },
    "not-found": { cls: "bg-gray-100 text-gray-600", icon: <Shield className="w-3 h-3 mr-1" />, label: t("rpki_notfound") },
  };
  const c = map[rpki] || map["not-found"];
  return <Badge className={`${c.cls} text-xs`}>{c.icon}RPKI {c.label}</Badge>;
};

// A single AS box used in the path list
const AsBox = ({ asn, name, cc, highlight }) => (
  <a
    href={`https://bgp.tools/as/${asn}`}
    target="_blank"
    rel="noopener noreferrer"
    title={name ? `AS${asn} — ${name}` : `AS${asn}`}
    className={`rounded-md border px-2.5 py-1.5 bg-white hover:shadow-sm transition block ${
      highlight ? "border-amber-500/70" : "border-gray-200"
    }`}
  >
    <div className="flex items-center gap-1.5">
      <Flag cc={cc} />
      <span className={`font-mono text-xs font-bold ${highlight ? "text-amber-700" : "text-primary"}`}>AS{asn}</span>
    </div>
    {name && <div className="text-[11px] text-gray-500 truncate max-w-[150px]">{name}</div>}
  </a>
);

// Merged AS-path fan diagram: SVG curved edges behind absolutely-positioned nodes.
// Nodes are laid out in columns by longest-path depth (shared ASes merge).
const AsPathDiagram = ({ routes }) => {
  const best = routes.find((r) => r.selected) || routes[0];
  const bp = best?.as_path || [];
  const bestEdges = new Set();
  for (let i = 1; i < bp.length; i++) bestEdges.add(`${bp[i - 1]}->${bp[i]}`);

  const info = new Map();
  const adj = new Map();
  const indeg = new Map();
  const ensure = (asn, name, cc) => {
    if (!info.has(asn)) info.set(asn, { asn, name, cc });
    if (!adj.has(asn)) adj.set(asn, new Set());
    if (!indeg.has(asn)) indeg.set(asn, 0);
  };
  for (const r of routes) {
    const p = r.as_path || [];
    for (let i = 0; i < p.length; i++) ensure(p[i], r.as_names?.[i], r.as_countries?.[i]);
    for (let i = 1; i < p.length; i++) {
      if (!adj.get(p[i - 1]).has(p[i])) {
        adj.get(p[i - 1]).add(p[i]);
        indeg.set(p[i], indeg.get(p[i]) + 1);
      }
    }
  }
  if (info.size === 0) return null;

  // Longest-path layering (Kahn topological order)
  const col = new Map([...info.keys()].map((k) => [k, 0]));
  const indeg2 = new Map(indeg);
  const queue = [...info.keys()].filter((k) => indeg2.get(k) === 0);
  while (queue.length) {
    const a = queue.shift();
    for (const b of adj.get(a)) {
      col.set(b, Math.max(col.get(b), col.get(a) + 1));
      indeg2.set(b, indeg2.get(b) - 1);
      if (indeg2.get(b) === 0) queue.push(b);
    }
  }
  const cols = [];
  for (const [asn, c] of col) (cols[c] = cols[c] || []).push(asn);
  const numCols = cols.length;
  const maxRows = Math.max(1, ...cols.map((c) => (c ? c.length : 0)));

  const BW = 158, BH = 46, CW = 210, RH = 66, PADX = 10, PADY = 10;
  const width = PADX * 2 + (numCols - 1) * CW + BW;
  const height = PADY * 2 + maxRows * RH;
  const pos = new Map();
  cols.forEach((arr, c) => {
    if (!arr) return;
    const offY = PADY + (maxRows * RH - arr.length * RH) / 2;
    arr.forEach((asn, i) => pos.set(asn, { x: PADX + c * CW, y: offY + i * RH + (RH - BH) / 2 }));
  });
  const edges = [];
  for (const [a, nexts] of adj) for (const b of nexts) edges.push([a, b]);

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ position: "relative", width, height, minWidth: "100%" }}>
        <svg width={width} height={height} style={{ position: "absolute", left: 0, top: 0 }}>
          {edges.map(([a, b], i) => {
            const pa = pos.get(a), pb = pos.get(b);
            if (!pa || !pb) return null;
            const x1 = pa.x + BW, y1 = pa.y + BH / 2, x2 = pb.x, y2 = pb.y + BH / 2;
            const mx = (x1 + x2) / 2;
            const isBest = bestEdges.has(`${a}->${b}`);
            return (
              <path key={i} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                fill="none" stroke={isBest ? "#b45309" : "#cbd5e1"}
                strokeWidth={isBest ? 2.5 : 1.25} strokeDasharray={isBest ? "" : "4 3"} />
            );
          })}
        </svg>
        {[...info.values()].map((n) => {
          const p = pos.get(n.asn);
          if (!p) return null;
          const onBest = bp.includes(n.asn);
          return (
            <a key={n.asn} href={`https://bgp.tools/as/${n.asn}`} target="_blank" rel="noopener noreferrer"
              title={n.name ? `AS${n.asn} — ${n.name}` : `AS${n.asn}`}
              style={{ position: "absolute", left: p.x, top: p.y, width: BW, height: BH }}
              className={`rounded-lg border bg-white px-2 flex flex-col justify-center hover:shadow-sm ${
                onBest ? "border-amber-600 border-2" : "border-gray-300"
              }`}>
              <div className="flex items-center gap-1.5">
                <Flag cc={n.cc} />
                <span className={`font-mono text-[11px] font-bold ${onBest ? "text-amber-700" : "text-gray-700"}`}>AS{n.asn}</span>
              </div>
              {n.name && <div className="text-[10px] text-gray-500 truncate">{n.name}</div>}
            </a>
          );
        })}
      </div>
    </div>
  );
};

// BGP Route Result Component
const BGPRouteResult = ({ data }) => {
  if (!data) return null;

  const routes = data.routes || [];
  const best = routes.find((r) => r.selected) || routes[0] || {};
  const bpath = best.as_path || [];
  const oi = bpath.length - 1;
  const srcAsn = bpath[oi];
  const srcName = best.as_names?.[oi];
  const srcCc = best.as_countries?.[oi];
  const allComms = [
    ...(best.communities || []),
    ...(best.large_communities || []),
    ...(best.ext_communities || []),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="bgp-result"
    >
      {/* Show resolved hostname info */}
      {data.resolved_from && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
          <span className="text-blue-700">
            <span className="font-medium">{data.resolved_from}</span> → <span className="font-mono">{data.resolved_ip}</span>
          </span>
        </div>
      )}

      {routes.length === 0 ? (
        <Card className="bg-white"><CardContent className="py-8 text-center text-gray-400">{t("no_routes")}</CardContent></Card>
      ) : (
        <>
          {/* Header: prefix, source AS, origin, RPKI */}
          <Card className="bg-white overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/70">
              <span className="font-mono text-xs text-gray-500">$ show route for {data.prefix} all</span>
            </div>
            <CardContent className="py-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t("prefix_announced")}</div>
                  <div className="font-mono text-primary font-semibold">{data.prefix}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t("source_as")}</div>
                  <div className="flex items-center gap-1.5">
                    <Flag cc={srcCc} />
                    <span className="font-mono text-sm font-semibold">AS{srcAsn}</span>
                    {srcName && <span className="text-sm text-gray-600 truncate">· {srcName}</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t("origin")}</div>
                  <div className="font-mono text-sm text-gray-700">{best.origin || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t("rpki")}</div>
                  {best.rpki ? <RpkiBadge rpki={best.rpki} /> : <span className="text-sm text-gray-400">—</span>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Path list (Seçili / Alternatif) */}
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <RouteIcon className="w-4 h-4 text-primary" />
                {t("paths_title")} ({routes.length} {t("path_unit")})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {routes.map((route, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-2 rounded-lg ${route.selected ? "bg-primary/5" : "hover:bg-gray-50"}`}>
                  <span className={`w-20 shrink-0 text-[11px] uppercase font-medium ${route.selected ? "text-primary" : "text-gray-400"}`}>
                    {route.selected ? t("selected") : t("alternative")}
                  </span>
                  <div className="flex items-center gap-1 flex-wrap flex-1">
                    {route.as_path.map((asn, i) => (
                      <div key={i} className="flex items-center">
                        <AsBox asn={asn} name={route.as_names?.[i]} cc={route.as_countries?.[i]} highlight={route.selected} />
                        {i < route.as_path.length - 1 && <ArrowRight className="w-3 h-3 mx-1 text-gray-300 shrink-0" />}
                      </div>
                    ))}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {route.rpki && <RpkiBadge rpki={route.rpki} />}
                    <span className="text-[11px] text-gray-400 whitespace-nowrap">LP {route.local_pref}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Community */}
          {allComms.length > 0 && (
            <Card className="bg-white">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">{t("community")}</CardTitle></CardHeader>
              <CardContent className="flex items-center gap-1 flex-wrap">
                {allComms.map((comm, i) => (
                  <span key={i} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono">{comm}</span>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <TerminalOutput content={data.raw} title={t("bgp_raw")} />
    </motion.div>
  );
};

// Whois Result Component
const WhoisResult = ({ data }) => {
  if (!data) return null;

  const parsed = data.parsed || {};
  const importantFields = ['netname', 'descr', 'country', 'org-name', 'admin-c', 'tech-c', 'status', 'created', 'last-modified'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="whois-result"
    >
      {Object.keys(parsed).length > 0 && (
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{t("parsed_info")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
              {importantFields.map((field) => {
                const value = parsed[field] || parsed[field.replace('-', '_')];
                if (!value) return null;
                return (
                  <div key={field} className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-sm text-gray-500 capitalize">{field.replace(/-/g, ' ')}</span>
                    <span className="text-sm font-mono">{value}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <TerminalOutput content={data.raw} title={t("whois_raw")} />
    </motion.div>
  );
};

// Speedtest Component - Self-hosted, browser-measured + CLI commands
// The browser is the stopwatch. Download pulls a plain static file served by
// nginx (no proxy/buffering in the way); upload posts to the backend sink;
// latency is a HEAD to the same file. Below the click-test we also expose
// curl/wget and iperf3 commands so users can test from a terminal.
// Speed = (bytes * 8) / seconds / 1e6 = Mbps
// Static files must exist under /speedtest/ (see docs/FULL_SETUP_GUIDE.md).
const SPEEDTEST_DOWNLOAD_URL = process.env.REACT_APP_SPEEDTEST_URL || "/speedtest/100MB.bin";
const SPEEDTEST_PING_COUNT = 10;                       // take the minimum RTT of N pings
const SPEEDTEST_PING_WARMUP = 2;                       // throwaway requests to open + reuse the connection
const SPEEDTEST_DOWNLOAD_STREAMS = 6;                  // parallel download connections
const SPEEDTEST_DOWNLOAD_MAX_MS = 12000;               // ~12 s measurement window
const SPEEDTEST_UPLOAD_BYTES = 20 * 1000 * 1000;       // 20 MB blob
const SPEEDTEST_HOST = (typeof window !== "undefined" && window.location.host) || "lg.netlen.com";
const SPEEDTEST_ORIGIN = (typeof window !== "undefined" && window.location.origin) || `https://${SPEEDTEST_HOST}`;

// A single copy-to-clipboard terminal command row.
const CmdRow = ({ label, cmd }) => (
  <div className="flex items-center gap-2 bg-gray-900 rounded-md px-3 py-2">
    {label && <span className="text-gray-400 text-xs shrink-0 w-20">{label}</span>}
    <code className="flex-1 overflow-x-auto whitespace-nowrap text-gray-100 font-mono text-xs">{cmd}</code>
    <button
      onClick={() => { navigator.clipboard.writeText(cmd); toast.success(t("copied")); }}
      className="p-1 hover:bg-gray-700 rounded shrink-0"
      title="Kopyala"
    >
      <Copy className="w-3.5 h-3.5 text-gray-300" />
    </button>
  </div>
);

// --- Speedometer gauge (SVG) ---
const gPolar = (cx, cy, r, deg) => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};
const gArc = (cx, cy, r, a0, a1) => {
  const [x0, y0] = gPolar(cx, cy, r, a0);
  const [x1, y1] = gPolar(cx, cy, r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
};
const SpeedGauge = ({ value, max = 1000, label }) => {
  const cx = 150, cy = 150, r = 112, a0 = 135, span = 270;
  const v = Math.max(0, Math.min(value || 0, max));
  const aV = a0 + (v / max) * span;
  const [nx, ny] = gPolar(cx, cy, r - 26, aV);
  return (
    <svg viewBox="0 0 300 250" className="w-full max-w-[300px]">
      <defs>
        <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
      <path d={gArc(cx, cy, r, a0, a0 + span)} fill="none" stroke="#1e3a5f" strokeWidth="14" strokeLinecap="round" />
      <path d={gArc(cx, cy, r, a0, aV)} fill="none" stroke="url(#gaugeGrad)" strokeWidth="14" strokeLinecap="round" />
      {[0, 250, 500, 750, 1000].map((t) => {
        const [tx, ty] = gPolar(cx, cy, r + 20, a0 + (t / max) * span);
        return <text key={t} x={tx} y={ty} fill="#64748b" fontSize="11" textAnchor="middle" dominantBaseline="middle">{t}</text>;
      })}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#e2e8f0" strokeWidth="3" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="7" fill="#0f2140" stroke="#64748b" strokeWidth="2" />
      <text x={cx} y={cy - 42} fill="#94a3b8" fontSize="13" textAnchor="middle">{label}</text>
      <text x={cx} y={cy - 2} fill="#fff" fontSize="40" fontWeight="800" textAnchor="middle">{value != null ? value.toFixed(1) : "—"}</text>
      <text x={cx} y={cy + 20} fill="#94a3b8" fontSize="13" textAnchor="middle">Mbps</text>
    </svg>
  );
};
const SpeedChart = ({ samples }) => {
  const W = 480, H = 170, padL = 34, padB = 20, padT = 8, padR = 8;
  const tMax = Math.max(12, ...samples.map((s) => s.t));
  const vMax = Math.max(100, ...samples.map((s) => Math.max(s.down || 0, s.up || 0))) * 1.1;
  const x = (t) => padL + (t / tMax) * (W - padL - padR);
  const y = (v) => H - padB - (v / vMax) * (H - padT - padB);
  const path = (key) => samples.map((s, i) => `${i ? "L" : "M"} ${x(s.t).toFixed(1)} ${y(s[key] || 0).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {[0, 0.25, 0.5, 0.75, 1].map((f) => {
        const yy = y(vMax * f);
        return (
          <g key={f}>
            <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="#1e293b" strokeWidth="1" />
            <text x={padL - 5} y={yy + 3} fill="#475569" fontSize="9" textAnchor="end">{Math.round(vMax * f)}</text>
          </g>
        );
      })}
      {samples.length > 1 && <path d={path("up")} fill="none" stroke="#8b5cf6" strokeWidth="2" />}
      {samples.length > 1 && <path d={path("down")} fill="none" stroke="#22d3ee" strokeWidth="2" />}
    </svg>
  );
};

const SpeedtestResult = () => {
  const [phase, setPhase] = useState("idle"); // idle|ping|download|upload|done|error
  const [ping, setPing] = useState(null);
  const [jitter, setJitter] = useState(null);
  const [downMbps, setDownMbps] = useState(0);
  const [upMbps, setUpMbps] = useState(0);
  const [error, setError] = useState(null);
  const [chart, setChart] = useState([]);
  const abortRef = useRef(null);
  const samplesRef = useRef([]);
  const lastDown = useRef(0);
  const lastUp = useRef(0);
  const testStart = useRef(0);
  const lastChartUi = useRef(0);
  const resultRef = useRef(null);

  const running = phase === "ping" || phase === "download" || phase === "upload";
  const fmt = (v) => (v == null ? "—" : v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2));

  const record = () => {
    const t = (performance.now() - testStart.current) / 1000;
    samplesRef.current.push({ t, down: lastDown.current, up: lastUp.current });
    const now = performance.now();
    if (now - lastChartUi.current > 150) {
      setChart([...samplesRef.current]);
      lastChartUi.current = now;
    }
  };

  // 1) Latency via the Resource Timing API. responseStart - requestStart is the
  //    request's network round-trip, EXCLUDING the TCP/TLS handshake — so it
  //    stays accurate even when the connection isn't reused (that setup time is
  //    what made the naive fetch-timed value ~7x too high vs ICMP). Same-origin
  //    (/speedtest/) gives us full timing detail. Falls back to wall-clock.
  const measurePing = async () => {
    performance.clearResourceTimings?.();
    const sample = async (q) => {
      const url = `${SPEEDTEST_DOWNLOAD_URL}?ping=${q}&t=${Date.now()}-${q}`;
      const t0 = performance.now();
      await fetch(url, { method: "HEAD", cache: "no-store" });
      const wall = performance.now() - t0;
      try {
        const abs = new URL(url, window.location.origin).href;
        const e = performance.getEntriesByName(abs).pop();
        if (e && e.responseStart > 0 && e.requestStart > 0) {
          return e.responseStart - e.requestStart;   // pure request round-trip
        }
      } catch (_) { /* fall back to wall time */ }
      return wall;
    };
    for (let w = 0; w < SPEEDTEST_PING_WARMUP; w++) {
      try { await sample(`warmup${w}`); } catch (_) { /* ignore */ }
    }
    const samples = [];
    for (let i = 0; i < SPEEDTEST_PING_COUNT; i++) {
      try { samples.push(await sample(i)); } catch (_) { /* ignore a failed sample */ }
    }
    performance.clearResourceTimings?.();
    if (!samples.length) throw new Error("Ping failed");
    const min = Math.min(...samples);
    const avg = samples.reduce((a, b) => a + b, 0) / samples.length;
    setPing(min);
    setJitter(Math.max(0, avg - min));
  };

  // 2) Download: parallel streams pulling the static file to saturate the link
  //    (a single TCP stream is often window-limited and under-reports).
  const measureDownload = async () => {
    const ac = new AbortController();
    abortRef.current = ac;
    let received = 0;
    const start = performance.now();
    let lastUi = start;

    const tick = () => {
      const now = performance.now();
      if (now - lastUi > 150) {
        const mbps = (received * 8) / ((now - start) / 1000) / 1e6;
        setDownMbps(mbps);
        lastDown.current = mbps;
        record();
        lastUi = now;
      }
    };

    const oneRequest = async (tag) => {
      const res = await fetch(
        `${SPEEDTEST_DOWNLOAD_URL}?t=${Date.now()}-${tag}`,
        { cache: "no-store", signal: ac.signal }
      );
      if (!res.ok) throw new Error(`Test file HTTP ${res.status}`);
      if ((res.headers.get("content-type") || "").includes("text/html")) {
        throw new Error("Test file missing (got HTML). Create /speedtest/100MB.bin");
      }
      if (!res.body) throw new Error("Streaming not supported");
      const reader = res.body.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        received += value.length;
        tick();
      }
    };

    const worker = async (id) => {
      let n = 0;
      while (performance.now() - start < SPEEDTEST_DOWNLOAD_MAX_MS) {
        try {
          await oneRequest(`${id}-${n++}`);
        } catch (e) {
          if (e.name === "AbortError") break;
          throw e;
        }
      }
    };

    const stopTimer = setTimeout(() => ac.abort(), SPEEDTEST_DOWNLOAD_MAX_MS);
    try {
      await Promise.all(
        Array.from({ length: SPEEDTEST_DOWNLOAD_STREAMS }, (_, i) => worker(i))
      );
    } finally {
      clearTimeout(stopTimer);
    }

    const elapsed = (performance.now() - start) / 1000;
    const finalMbps = elapsed > 0 ? (received * 8) / elapsed / 1e6 : 0;
    setDownMbps(finalMbps);
    lastDown.current = finalMbps;
    record();
  };

  // 3) Upload: push a 20 MB in-memory blob (XHR for live progress); server discards it.
  const measureUpload = () =>
    new Promise((resolve, reject) => {
      const blob = new Blob([new Uint8Array(SPEEDTEST_UPLOAD_BYTES)]);
      const start = performance.now();
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/speedtest/upload?t=${Date.now()}`);
      xhr.setRequestHeader("Content-Type", "application/octet-stream");
      xhr.upload.onprogress = (e) => {
        const el = (performance.now() - start) / 1000;
        if (el > 0 && e.loaded > 0) {
          const mbps = (e.loaded * 8) / el / 1e6;
          setUpMbps(mbps);
          lastUp.current = mbps;
          record();
        }
      };
      xhr.onload = () => {
        const el = (performance.now() - start) / 1000;
        const mbps = el > 0 ? (SPEEDTEST_UPLOAD_BYTES * 8) / el / 1e6 : 0;
        setUpMbps(mbps);
        lastUp.current = mbps;
        record();
        resolve();
      };
      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.send(blob);
    });

  const runTest = async () => {
    setError(null); setPing(null); setJitter(null); setDownMbps(0); setUpMbps(0);
    samplesRef.current = []; setChart([]); lastDown.current = 0; lastUp.current = 0;
    testStart.current = performance.now(); lastChartUi.current = 0;
    try {
      setPhase("ping"); await measurePing();
      setPhase("download"); await measureDownload();
      setPhase("upload"); await measureUpload();
      setPhase("done");
    } catch (e) {
      setError(e.message || "Test failed");
      setPhase("error");
    }
  };

  const stopTest = () => {
    if (abortRef.current) abortRef.current.abort();
    setPhase("idle");
  };

  const phaseLabel = {
    ping: t("st_measuring_ping"),
    download: t("st_measuring_down"),
    upload: t("st_measuring_up"),
  }[phase];

  const gaugeVal = phase === "upload" ? upMbps : downMbps;
  const gaugeLabel = phase === "upload" ? t("st_up_speed") : t("st_down_speed");

  const shareResult = () => {
    navigator.clipboard.writeText(
      `Netlen Speedtest — İndirme: ${fmt(downMbps)} Mbps · Yükleme: ${fmt(upMbps)} Mbps · Ping: ${fmt(ping)} ms · ${window.location.origin}`
    );
    toast.success(t("st_shared"));
  };
  const downloadPng = async () => {
    if (!resultRef.current) return;
    try {
      const canvas = await html2canvas(resultRef.current, { backgroundColor: "#0b1627", scale: 2 });
      const a = document.createElement("a");
      a.download = `netlen-speedtest-${Date.now()}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
      toast.success(t("st_png_done"));
    } catch (_) { toast.error(t("png_fail")); }
  };

  const fileBtn = "inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-primary/40 hover:text-primary transition";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="speedtest-result"
    >
      {/* Info row */}
      <Card className="bg-white">
        <CardContent className="py-3 grid grid-cols-1 md:grid-cols-3 gap-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
          <div className="flex items-center gap-2 pt-0">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <div><div className="font-semibold text-gray-800 text-sm">{t("st_server")}</div><div className="text-xs text-gray-400">{t("st_server_sub")}</div></div>
          </div>
          <div className="flex items-center gap-2 pt-3 md:pt-0 md:pl-4">
            <Monitor className="w-5 h-5 text-gray-400" />
            <div><div className="font-medium text-gray-700 text-sm">{t("st_browser")}</div><div className="text-xs text-gray-400">{t("st_browser_sub")}</div></div>
          </div>
          <div className="flex items-center gap-2 pt-3 md:pt-0 md:pl-4">
            <ShieldCheck className="w-5 h-5 text-green-500" />
            <div><div className="font-medium text-gray-700 text-sm">HTTPS</div><div className="text-xs text-gray-400">{t("st_https_sub")}</div></div>
          </div>
        </CardContent>
      </Card>

      {/* Dark result card */}
      <div ref={resultRef} className="rounded-2xl bg-[#0b1627] border border-white/5 p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-white font-semibold">{t("st_sample")}</span>
          {phase === "done" ? (
            <span className="text-green-400 text-sm flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {t("st_done")}</span>
          ) : running ? (
            <span className="text-cyan-300 text-sm flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> {phaseLabel}</span>
          ) : (
            <span className="text-gray-500 text-sm">{t("st_ready")}</span>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div className="flex justify-center">
            <SpeedGauge value={gaugeVal} label={gaugeLabel} />
          </div>
          <div>
            <div className="grid grid-cols-2 gap-4 pb-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full border border-cyan-400/40 flex items-center justify-center shrink-0"><ArrowDown className="w-4 h-4 text-cyan-400" /></span>
                <div><div className="text-xs text-gray-400">{t("st_down")}</div><div className="text-2xl font-bold text-white leading-tight">{fmt(downMbps)} <span className="text-sm font-normal text-gray-400">Mbps</span></div></div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full border border-violet-400/40 flex items-center justify-center shrink-0"><ArrowUp className="w-4 h-4 text-violet-400" /></span>
                <div><div className="text-xs text-gray-400">{t("st_up")}</div><div className="text-2xl font-bold text-white leading-tight">{fmt(upMbps)} <span className="text-sm font-normal text-gray-400">Mbps</span></div></div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 py-4 border-b border-white/5 text-center">
              <div><div className="text-[11px] text-gray-400">{t("st_ping")}</div><div className="text-white font-semibold">{fmt(ping)} <span className="text-xs text-gray-400">ms</span></div></div>
              <div><div className="text-[11px] text-gray-400">{t("st_jitter")}</div><div className="text-white font-semibold">{fmt(jitter)} <span className="text-xs text-gray-400">ms</span></div></div>
              <div><div className="text-[11px] text-gray-400">{t("st_ploss")}</div><div className="text-white font-semibold">%0</div></div>
            </div>
            <div className="pt-3">
              <div className="flex items-center justify-end gap-4 text-xs text-gray-400 mb-1">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-400" /> {t("st_down")}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> {t("st_up")}</span>
              </div>
              <SpeedChart samples={chart} />
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        {!running ? (
          <Button onClick={runTest} className="bg-primary text-white px-6" data-testid="speedtest-start">
            <RefreshCw className="w-4 h-4 mr-2" />
            {phase === "done" || phase === "error" ? t("st_restart") : t("st_start")}
          </Button>
        ) : (
          <Button onClick={stopTest} variant="outline" className="px-6">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("st_stop")}
          </Button>
        )}
        <Button variant="outline" onClick={shareResult} className="px-5"><Share2 className="w-4 h-4 mr-2" /> {t("st_share")}</Button>
        <Button variant="outline" onClick={downloadPng} className="px-5"><Download className="w-4 h-4 mr-2" /> {t("st_png")}</Button>
        {error && <span className="text-sm text-red-500">{error}</span>}
      </div>

      {/* Test files */}
      <Card className="bg-white">
        <CardContent className="py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Download className="w-6 h-6 text-primary" />
            <div>
              <div className="font-semibold text-gray-800">{t("st_files")}</div>
              <div className="text-sm text-gray-500">{t("st_files_sub")}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <a href={`${SPEEDTEST_ORIGIN}/speedtest/100MB.bin`} target="_blank" rel="noopener noreferrer" className={fileBtn}>100 MB <Download className="w-3.5 h-3.5" /></a>
            <a href={`${SPEEDTEST_ORIGIN}/speedtest/1GB.bin`} target="_blank" rel="noopener noreferrer" className={fileBtn}>1 GB <Download className="w-3.5 h-3.5" /></a>
            <a href={`${SPEEDTEST_ORIGIN}/speedtest/10GB.bin`} target="_blank" rel="noopener noreferrer" className={fileBtn}>10 GB <Download className="w-3.5 h-3.5" /></a>
          </div>
        </CardContent>
      </Card>

      {/* Tip */}
      <Card className="bg-white">
        <CardContent className="py-3 flex items-start gap-3">
          <span className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5"><Info className="w-4 h-4 text-primary" /></span>
          <div className="text-sm">
            <div className="font-semibold text-gray-700">{t("st_tip_title")}</div>
            <div className="text-gray-500">{t("st_tip_sub")}</div>
          </div>
        </CardContent>
      </Card>

      {/* iperf3 (terminal) */}
      <Card className="bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Terminal (iperf3 · port 5201)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <CmdRow label="Download" cmd={`iperf3 -c ${SPEEDTEST_HOST} -R`} />
          <CmdRow label="Upload" cmd={`iperf3 -c ${SPEEDTEST_HOST}`} />
          <CmdRow label={t("cmd_bidir")} cmd={`iperf3 -c ${SPEEDTEST_HOST} --bidir`} />
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Netlen network info cards
const NetworkInfoSection = () => {
  const copy = (val) => { navigator.clipboard.writeText(val); toast.success(t("copied")); };
  return (
    <section id="aginfo" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t("net_title")}</h2>
      <p className="text-gray-500 mt-1 mb-6">{t("net_sub")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: <Layers className="w-6 h-6 text-primary" />, label: "AS Number", value: "AS44620" },
          { icon: <Share2 className="w-6 h-6 text-primary" />, label: "AS-SET", value: "AS44620:AS-NETLEN" },
          { icon: <Monitor className="w-6 h-6 text-primary" />, label: "Test IPv4", value: "45.74.159.119" },
          { icon: <Monitor className="w-6 h-6 text-primary" />, label: "Test IPv6", value: "2a01:f900:10:f1::1" },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-start justify-between">
              {card.icon}
              <button onClick={() => copy(card.value)} className="p-1 hover:bg-gray-100 rounded" title={t("copy")}>
                <Copy className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="text-sm text-gray-500 mt-4">{card.label}</div>
            <div className="text-base font-bold text-gray-900 font-mono break-all leading-snug" title={card.value}>{card.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

// BGP community dictionary (AS44620) — cat is a stable key; use/desc are bilingual
const BGP_COMMUNITIES = [
  { c: "44620:300", cat: "lp", use: "DDoS scrubbing (ek)", useEn: "DDoS scrubbing (extra)", desc: "Normal duyurular korunur; ayrıca DDoS temizleme üzerinden de duyurulur.", descEn: "Normal announcements kept; also announced via DDoS scrubbing." },
  { c: "44620:400", cat: "lp", use: "Uluslararası scrubbing", useEn: "International via scrubbing", desc: "Uluslararası trafik DDoS temizlemeden; yurtiçi transitler yalnızca yurtiçi.", descEn: "International via DDoS scrubbing; domestic transits domestic-only." },
  { c: "44620:444", cat: "lp", use: "Sadece yurtiçi", useEn: "Domestic only", desc: "Yalnızca yurtiçi; yurtiçi transitlerde uluslararası bloklanır.", descEn: "Domestic only; international blocked on domestic transits." },
  { c: "44620:500", cat: "lp", use: "Sadece scrubbing", useEn: "Only via scrubbing", desc: "Yalnızca DDoS temizleme üzerinden; yurtiçi transitlerden çekilir.", descEn: "Announced only via DDoS scrubbing; withdrawn from domestic transits." },
  { c: "44620:666", cat: "blackhole", use: "Blackhole", useEn: "Blackhole", desc: "Hedef trafiği karadeliğe yönlendirir (kendi prefix'leri içinde /32 veya /128).", descEn: "Blackhole the destination (/32 or /128 within own prefixes)." },
  { c: "44620:0:nnn", cat: "lp", use: "Duyurma", useEn: "Do not announce", desc: "AS{nnn}'e duyurma.", descEn: "Do not announce to AS{nnn}." },
  { c: "44620:1:nnn", cat: "lp", use: "Duyur", useEn: "Announce", desc: "AS{nnn}'e duyur.", descEn: "Announce to AS{nnn}." },
  { c: "44620:10x:nnn", cat: "prepend", use: "AS-path prepend", useEn: "AS-path prepend", desc: "AS{nnn}'e x kez prepend uygula.", descEn: "Prepend x times to AS{nnn}." },
  { c: "44620:1000", cat: "info", use: "Rota kaynağı", useEn: "Route source", desc: "Müşteri VM BGP oturumundan öğrenildi.", descEn: "Learned from a customer VM BGP session." },
  { c: "44620:1010:nnn", cat: "info", use: "Rota kaynağı", useEn: "Route source", desc: "IX/peering AS{nnn}'ten öğrenildi.", descEn: "Learned from IX/peering AS{nnn}." },
  { c: "44620:nnn:1000", cat: "info", use: "Rota kaynağı", useEn: "Route source", desc: "Transit AS{nnn}'ten öğrenildi.", descEn: "Learned from transit AS{nnn}." },
  { c: "44620:216394:1", cat: "info", use: "Rota kaynağı", useEn: "Route source", desc: "Anti-DDoS müşteri AS216394'ten öğrenildi.", descEn: "Learned from anti-DDoS customer AS216394." },
  { c: "44620:1000:1", cat: "info", use: "RPKI Valid", useEn: "RPKI Valid", desc: "RPKI doğrulaması: geçerli.", descEn: "RPKI validation: valid." },
  { c: "44620:1000:2", cat: "info", use: "RPKI Not Found", useEn: "RPKI Not Found", desc: "RPKI doğrulaması: ROA bulunamadı.", descEn: "RPKI validation: ROA not found." },
  { c: "44620:1000:3", cat: "info", use: "RPKI Invalid", useEn: "RPKI Invalid", desc: "RPKI doğrulaması: geçersiz.", descEn: "RPKI validation: invalid." },
];

const COMMUNITY_CAT_COLOR = {
  info: "bg-blue-50 text-blue-700",
  lp: "bg-amber-50 text-amber-700",
  prepend: "bg-purple-50 text-purple-700",
  blackhole: "bg-red-50 text-red-700",
};
const COMMUNITY_CAT_KEY = { info: "cat_info", lp: "cat_lp", prepend: "cat_prepend", blackhole: "cat_blackhole" };

const BgpCommunitiesSection = () => {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const en = CURRENT_LANG === "en";
  const cats = ["all", "info", "lp", "prepend", "blackhole"];
  const catLabel = (k) => (k === "all" ? t("comm_all") : t(COMMUNITY_CAT_KEY[k]));
  const uText = (r) => (en ? r.useEn : r.use);
  const dText = (r) => (en ? r.descEn : r.desc);
  const rows = BGP_COMMUNITIES.filter(
    (r) =>
      (filter === "all" || r.cat === filter) &&
      (q === "" || `${r.c} ${uText(r)} ${dText(r)}`.toLowerCase().includes(q.toLowerCase()))
  );
  return (
    <section id="communities" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">BGP Communities</h2>
          <p className="text-gray-500 mt-1">{t("comm_sub")}</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("comm_search")} className="pl-9 h-10 bg-white" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {cats.map((c) => (
          <button key={c} onClick={() => setFilter(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${filter === c ? "bg-primary text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {catLabel(c)}
          </button>
        ))}
      </div>
      <Card className="bg-white overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="py-3.5 px-5 font-semibold w-40">{t("th_community")}</th>
                <th className="py-3.5 px-5 font-semibold w-44">{t("th_category")}</th>
                <th className="py-3.5 px-5 font-semibold">{t("th_usage")}</th>
                <th className="py-3.5 px-5 font-semibold hidden lg:table-cell">{t("th_desc")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40 transition-colors">
                  <td className="py-3.5 px-5">
                    <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md whitespace-nowrap">{r.c}</span>
                  </td>
                  <td className="py-3.5 px-5">
                    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${COMMUNITY_CAT_COLOR[r.cat] || "bg-gray-100 text-gray-600"}`}>{catLabel(r.cat)}</span>
                  </td>
                  <td className="py-3.5 px-5 text-gray-800 font-medium whitespace-nowrap">{uText(r)}</td>
                  <td className="py-3.5 px-5 text-gray-500 hidden lg:table-cell">{dText(r)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-gray-400">{t("no_results")}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
};

// Main Home Component
const Home = () => {
  const [routers, setRouters] = useState([]);
  const [selectedRouter, setSelectedRouter] = useState("");
  const [selectedTool, setSelectedTool] = useState("ping");
  const [target, setTarget] = useState("");
  const [ipVersion, setIPVersion] = useState("4");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [autoExecute, setAutoExecute] = useState(false);
  const [lang, setLang] = useState(CURRENT_LANG);
  const switchLang = (l) => {
    CURRENT_LANG = l;
    try { localStorage.setItem("lg_lang", l); } catch (_) { /* ignore */ }
    setLang(l);
  };
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch (_) { /* ignore */ }
  }, [lang]);

  // Parse URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const toolParam = params.get('tool');
    const targetParam = params.get('target');
    const ipParam = params.get('ip') || params.get('ip_version');
    
    if (toolParam) {
      const toolMap = {
        'ping': 'ping',
        'mtr': 'mtr',
        'traceroute': 'mtr',
        'bgp': 'bgp-route',
        'bgp-route': 'bgp-route',
        'route': 'bgp-route',
        'whois': 'whois'
      };
      const mappedTool = toolMap[toolParam.toLowerCase()];
      if (mappedTool) {
        setSelectedTool(mappedTool);
      }
    }
    
    if (targetParam) {
      setTarget(decodeURIComponent(targetParam));
      setAutoExecute(true);
    }
    
    if (ipParam && (ipParam === '4' || ipParam === '6')) {
      setIPVersion(ipParam);
    }
  }, []);

  // Fetch routers on mount
  useEffect(() => {
    const fetchRouters = async () => {
      try {
        const response = await axios.get(`${API}/routers`);
        const data = Array.isArray(response.data) ? response.data : [];
        setRouters(data);
        if (data.length > 0) {
          setSelectedRouter(data[0].id);
        }
      } catch (e) {
        console.error("Failed to fetch routers:", e);
        toast.error(t("routers_fail"));
        setRouters([]);
      }
    };
    fetchRouters();
  }, []);

  // Clear result when tool changes (only if not auto-executing)
  useEffect(() => {
    if (!autoExecute) {
      setResult(null);
      setError(null);
    }
  }, [selectedTool, autoExecute]);

  const executeQuery = useCallback(async () => {
    if (!target.trim()) {
      toast.error(t("enter_target"));
      return;
    }

    if (selectedTool !== "whois" && !selectedRouter) {
      toast.error(t("select_router"));
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      let response;
      const basePayload = {
        router_id: selectedRouter,
        target: target.trim(),
        ip_version: ipVersion
      };

      switch (selectedTool) {
        case "ping":
          response = await axios.post(`${API}/ping`, { ...basePayload, count: 5 });
          break;
        case "mtr":
          response = await axios.post(`${API}/mtr`, { ...basePayload, report_cycles: 3 });
          break;
        case "bgp-route":
          response = await axios.post(`${API}/bgp-route`, { ...basePayload, prefix: target.trim() });
          break;
        case "whois":
          response = await axios.post(`${API}/whois`, { query: target.trim() });
          break;
        default:
          throw new Error("Unknown tool");
      }

      setResult(response.data);
      
      // Only show error if status is error AND there's an actual error message AND no raw data
      if (response.data.status === "error" && response.data.error && !response.data.data?.raw) {
        toast.error(response.data.error);
        setError(response.data.error);
      } else {
        toast.success(t("query_ok"));
      }
    } catch (e) {
      console.error("Query failed:", e);
      const errorMsg = e.response?.data?.detail || e.message || "Query failed";
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [target, selectedTool, selectedRouter, ipVersion]);

  // Auto-execute query if URL params provided
  useEffect(() => {
    if (autoExecute && selectedRouter && target) {
      setAutoExecute(false);
      executeQuery();
    }
  }, [autoExecute, selectedRouter, target, executeQuery]);

  const selectedRouterData = Array.isArray(routers) ? routers.find(r => r.id === selectedRouter) : null;
  const currentTool = TOOLS.find(t => t.id === selectedTool);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="looking-glass-app">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/netlen-logo.png" alt="Netlen" className="h-7 w-auto" />
            <span className="hidden sm:inline border-l border-gray-200 pl-3 font-semibold text-gray-800">Looking Glass</span>
          </div>
          <div className="hidden md:flex items-center gap-7 text-sm text-gray-600">
            <a href="#test" className="hover:text-primary transition-colors">{t("nav_tools")}</a>
            <a href="#aginfo" className="hover:text-primary transition-colors">{t("nav_netinfo")}</a>
            <a href="#communities" className="hover:text-primary transition-colors">{t("nav_comm")}</a>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm">
              <button onClick={() => switchLang("tr")} className={lang === "tr" ? "text-primary font-semibold" : "text-gray-400 hover:text-gray-600"}>TR</button>
              <span className="text-gray-300">/</span>
              <button onClick={() => switchLang("en")} className={lang === "en" ? "text-primary font-semibold" : "text-gray-400 hover:text-gray-600"}>EN</button>
            </div>
            <a href="https://netlen.com.tr" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-primary/30 text-primary px-3 py-1.5 text-sm font-medium hover:bg-primary/5 transition-colors">
              netlen.com.tr <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundColor: "#081228",
          backgroundImage:
            "linear-gradient(90deg, rgba(8,18,40,0.98) 0%, rgba(8,18,40,0.88) 38%, rgba(8,18,40,0.3) 100%), url('/assets/hero-globe.png')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-28">
          <div className="text-cyan-400 text-xs font-semibold tracking-[0.2em] mb-3">
            {selectedTool === "speedtest" ? t("hero_label_speed") : t("hero_label")}
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-[1.1] tracking-tight">
            {selectedTool === "speedtest" ? (
              <>{t("hero_title_speed")}</>
            ) : (
              <>{t("hero_title_1")}<br />{t("hero_title_2")}</>
            )}
          </h1>
          <p className="text-blue-100/70 mt-4 max-w-xl text-base md:text-lg">
            {selectedTool === "speedtest" ? t("hero_sub_speed") : t("hero_sub")}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-blue-50">
              <Globe className="w-4 h-4" /> IPv4 &amp; IPv6
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-blue-50">
              <MapPin className="w-4 h-4" /> {t("pill_loc")}
            </span>
          </div>
        </div>
      </div>

      {/* Query Card (overlaps hero) */}
      <div id="test" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
          {/* Main Query Card */}
          <Card className="bg-white shadow-lg border-0" data-testid="query-card">
            <CardContent className="p-6">
              {/* Router Selector - shown on all tabs for layout consistency */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">{t("f_router")}</label>
                    <Select value={selectedRouter} onValueChange={setSelectedRouter} data-testid="router-select">
                      <SelectTrigger className="w-full h-12 bg-white border-gray-200" data-testid="router-select-trigger">
                        <SelectValue placeholder={t("f_router")}>
                          {selectedRouterData && (
                            <div className="flex items-center gap-2">
                              <Server className="w-4 h-4 text-primary" />
                              <span>{selectedRouterData.name}</span>
                              <span className="text-gray-400 text-xs">({selectedRouterData.location})</span>
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {routers.map((router) => (
                          <SelectItem key={router.id} value={router.id} data-testid={`router-option-${router.id}`}>
                            <div className="flex items-center gap-2">
                              <Server className="w-4 h-4 text-primary" />
                              <span>{router.name}</span>
                              <span className="text-gray-400 text-xs ml-2">{router.location}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-full md:w-40">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">{t("f_ipver")}</label>
                    <Select value={ipVersion} onValueChange={setIPVersion} data-testid="ip-version-select">
                      <SelectTrigger className="w-full h-12 bg-white border-gray-200" data-testid="ip-version-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4" data-testid="ipv4-option">IPv4</SelectItem>
                        <SelectItem value="6" data-testid="ipv6-option">IPv6</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

              {/* Tool Tabs */}
              <Tabs value={selectedTool} onValueChange={setSelectedTool} className="mb-6">
                <TabsList className="w-full h-auto p-1 bg-gray-100 grid grid-cols-5 gap-1" data-testid="tool-tabs">
                  {TOOLS.map((tool) => {
                    const Icon = tool.icon;
                    return (
                      <TabsTrigger
                        key={tool.id}
                        value={tool.id}
                        className="flex flex-col items-center gap-1 py-3 px-2 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
                        data-testid={`tool-tab-${tool.id}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-xs font-medium">{tool.name}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>

              {/* Search Input - Hidden for Speedtest */}
              {selectedTool !== "speedtest" && (
                <>
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder={
                          selectedTool === "bgp-route"
                            ? t("ph_bgp")
                            : selectedTool === "whois"
                            ? t("ph_whois")
                            : t("ph_default")
                        }
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && executeQuery()}
                        className="h-14 pl-12 pr-4 text-lg bg-white border-gray-200 input-glow"
                        data-testid="target-input"
                      />
                    </div>
                    <Button
                      onClick={executeQuery}
                      disabled={loading}
                      className="h-14 px-8 bg-primary hover:bg-accent text-white font-semibold"
                      data-testid="execute-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t("btn_running")}
                        </>
                      ) : (
                        <>
                          <ArrowRight className="w-5 h-5 mr-2" />
                          {t("btn_execute")}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
      </div>

      {/* Results Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Speedtest - Always show iframe when selected */}
        {selectedTool === "speedtest" && (
          <SpeedtestResult />
        )}

        {/* Other tools - show loading/error/results */}
        {selectedTool !== "speedtest" && loading && (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <div className="text-center">
              <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-gray-500">{t("executing")} {currentTool?.name}...</p>
            </div>
          </div>
        )}

        {selectedTool !== "speedtest" && !loading && error && (
          <div className="w-full" data-testid="error-state">
            <Card className="bg-red-50 border-red-200 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-red-800">{t("result_failed")}</h3>
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {selectedTool !== "speedtest" && !loading && result && !error && (
          <Card className="bg-white shadow-lg border-0" data-testid="result-section">
            <CardContent className="p-6">
              {/* Result Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    {currentTool?.name} {t("results")}
                  </h2>
                  {result.router && (
                    <Badge variant="secondary" className="ml-2">
                      <MapPin className="w-3 h-3 mr-1" />
                      {result.router}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={executeQuery}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t("refresh")}
                </Button>
              </div>

              {/* Result Content */}
              {selectedTool === "ping" && <PingResult data={result.data} router={result.router} target={result.target} />}
              {selectedTool === "mtr" && <MTRResult data={result.data} router={result.router} target={result.target} />}
              {selectedTool === "bgp-route" && <BGPRouteResult data={result.data} />}
              {selectedTool === "whois" && <WhoisResult data={result.data} />}
            </CardContent>
          </Card>
        )}

        {selectedTool !== "speedtest" && !loading && !result && !error && (
          <div className="text-center py-8" data-testid="empty-state">
            <Network className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-medium text-gray-600 mb-1">{t("empty_title")}</h3>
            <p className="text-gray-400 text-sm">{t("empty_sub")}</p>
          </div>
        )}
      </div>

      {/* Netlen network info */}
      <NetworkInfoSection />

      {/* BGP Communities */}
      <BgpCommunitiesSection />

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-10 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-4">
              <img src="/assets/netlen-logo.png" alt="Netlen" className="h-6 w-auto" />
              <span className="text-gray-400">© {new Date().getFullYear()} {t("footer_copy")}</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://www.netlen.com.tr/peering-politikasi" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">{t("footer_peering")}</a>
              <a href="https://netlen.com.tr" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">{t("footer_contact")}</a>
              <a href="https://netlen.com.tr" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">{t("footer_terms")}</a>
            </div>
          </div>
        </div>
      </footer>

      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
